import React, { useEffect, useState } from 'react';
import { Box, IconButton, Paper, Slide, Typography, Tooltip } from '@mui/material';
import { SmartToyOutlined, Close, OpenInFull } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import AssistantChat from './AssistantChat';

/**
 * Floating chat bubble rendered inside Layout. Visible to any user with
 * assistant access (admin/super_admin always; viewer/app_viewer only when a
 * super admin has granted them). Auto-hidden on /assistant (redundant with
 * the full page).
 */
const AssistantBubble: React.FC = () => {
  const { user } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState<boolean | null>(null);

  const hasAssistantAccess = !!user?.assistant_enabled;

  useEffect(() => {
    if (!hasAssistantAccess) { setEnabled(false); return; }
    let cancelled = false;
    apiService.getAssistantStatus()
      .then(status => { if (!cancelled) setEnabled(!!status.enabled); })
      .catch(() => { if (!cancelled) setEnabled(false); });
    return () => { cancelled = true; };
  }, [hasAssistantAccess]);

  if (!enabled) return null;
  if (location.pathname.startsWith('/assistant')) return null;

  const border = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
  const surface = isDark ? '#16162a' : '#ffffff';
  const textP = isDark ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.9)';
  const textM = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)';

  return (
    <>
      {/* Chat panel */}
      <Slide in={open} direction="up" mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 96,
            right: 24,
            width: 380,
            height: 560,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: surface,
            border: `1px solid ${border}`,
            borderRadius: 3,
            overflow: 'hidden',
            zIndex: (t) => t.zIndex.modal - 1,
            boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${border}` }}>
            <SmartToyOutlined sx={{ color: '#3B82F6', fontSize: 20 }} />
            <Typography sx={{ fontWeight: 600, color: textP, fontSize: '0.9rem', flex: 1 }}>
              Assistant
            </Typography>
            <Tooltip title="Open full page">
              <IconButton size="small" onClick={() => { setOpen(false); navigate('/assistant'); }} sx={{ color: textM }}>
                <OpenInFull sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: textM }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <AssistantChat compact />
          </Box>
        </Paper>
      </Slide>

      {/* Floating action button */}
      <Tooltip title={open ? 'Close assistant' : 'Ask the assistant'} placement="left">
        <IconButton
          onClick={() => setOpen(v => !v)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 56,
            height: 56,
            bgcolor: '#3B82F6',
            color: '#ffffff',
            boxShadow: '0 12px 30px rgba(59,130,246,0.4)',
            '&:hover': { bgcolor: '#2563EB' },
            zIndex: (t) => t.zIndex.modal - 1,
          }}
        >
          {open ? <Close /> : <SmartToyOutlined />}
        </IconButton>
      </Tooltip>
    </>
  );
};

export default AssistantBubble;
